import { Button, Col, Image, message, Progress, Row, Space, Steps, Upload, UploadProps } from 'antd'
import React, { FC, useEffect, useState } from 'react'
import { RcFile } from 'antd/es/upload'
import { useMutation, useQuery } from '@apollo/client'
import classNames from 'classnames'
import {
  CheckOutlined,
  LoadingOutlined,
  MinusSquareOutlined,
  PlusSquareOutlined,
  RedoOutlined,
  UploadOutlined
} from '@ant-design/icons'
import { uploaderService } from '../../shared/service'
import {
  MassUploadAddDifferentImagesToItemsMutation,
  MassUploadAddSkuDoNotHaveAnyImagesMutation,
  MassUploadReplaceDuplicatedMutation,
  MassUploadSummary
} from '../graphql/mass-upload.graphql'
import { translate } from '../../../core'
import { Container, ShowIf } from '../../../core/component'
import { resolvePathFile } from '../../../core/until'
import { ZipIcon } from '../../../component/icon'
import style from './massUpload.module.scss'

const CompletedActionIcon: FC<{ completed: boolean }> = ({completed}) =>
  <ShowIf
    condition={completed}
    replacement={<></>}
  >
    <CheckOutlined style={{color: 'green', fontSize: 20}}/>
  </ShowIf>

export const MassUpload = () => {
  const [messageApi, contextHolder] = message.useMessage()
  const [file, setFile] = useState<RcFile>()
  const [percentage, setPercentage] = useState<number>(0)
  const [currentStep, setCurrentStep] = useState<number>(0)
  const [directoryId, setDirectoryId] = useState<string>()
  const [expand, setExpand] = useState<{
    duplicated: boolean
    different: boolean
  }>()
  const [extractOnError, setExtractOnError] = useState<boolean>()

  const extract = async () => {
    if (!directoryId)
      return

    setExtractOnError(false)

    try {
      await uploaderService.extract(directoryId)
      setCurrentStep(pre => ++pre)
    } catch {
      setExtractOnError(true)
    }
  }

  useEffect(() => {
    extract()
  }, [directoryId])

  const [completedActions, setCompletedActions] = useState<{
    noImages: boolean
    duplicated: boolean
    different: boolean
  }>()

  const uploadProps: UploadProps = {
    beforeUpload: async file => {
      setFile(file)
      setPercentage(0)
      setCurrentStep(pre => ++pre)

      const uploadId = await uploaderService.uploadChunks(
        file,
        res => setPercentage(res.percentage))

      setDirectoryId(uploadId)
      setCurrentStep(pre => ++pre)
    },
    showUploadList: false,
  }

  const {data, loading} = useQuery(MassUploadSummary, {
    variables: {
      directoryId: directoryId ?? '',
    },
    skip: currentStep !== 3,
  })

  const [addSkuDoNotHaveAnyImages, {loading: inProgressAddSkuDoNotHaveAnyImages}] = useMutation(MassUploadAddSkuDoNotHaveAnyImagesMutation, {
    variables: {
      directoryId: directoryId ?? '',
    },
    onCompleted: () => {
      setCompletedActions(pre => ({...pre as any, noImages: true}))
      messageApi.success(translate('update_success_message'))
    },
    onError: error => messageApi.error(error.message),
  })

  const [addDifferentImagesToItems, {loading: inProgressAddDifferentImagesToItems}] = useMutation(MassUploadAddDifferentImagesToItemsMutation, {
    variables: {
      directoryId: directoryId ?? '',
    },
    onCompleted: () => {
      setCompletedActions(pre => ({...pre as any, different: true}))
      messageApi.success(translate('update_success_message'))
    },
    onError: error => messageApi.error(error.message),
  })

  const [replaceDuplicated, {loading: inProgressReplaceDuplicated}] = useMutation(MassUploadReplaceDuplicatedMutation, {
    variables: {
      directoryId: directoryId ?? '',
    },
    onCompleted: () => {
      setCompletedActions(pre => ({...pre as any, duplicated: true}))
      messageApi.success(translate('update_success_message'))
    },
    onError: error => messageApi.error(error.message),
  })

  const getStepTitle = (step: number, stepTitle: React.ReactNode) => {
    if (step === currentStep)
      return stepTitle

    if (currentStep < step)
      return translate('waiting')

    return translate('finished')
  }

  return <>
    <Button
      type='primary'
      size='small'
      icon={<RedoOutlined/>}
      onClick={() => {
        setDirectoryId(undefined)
        setCurrentStep(0)
        setFile(undefined)
      }}
    >
      {translate('reset', 'steps')}
    </Button>

    <Steps
      className='mt-3'
      direction='vertical'
      current={currentStep}
      items={[
        {
          title: getStepTitle(0, <Upload
            {...uploadProps}
          >
            <Button
              size='small'
              type='primary'
              icon={<UploadOutlined/>}
            >
              {translate('upload')}
            </Button>
          </Upload>),
          description: [
            translate('upload', 'zip', 'file'),
            file ? ' : ' : null,
            file?.name,
          ]
            .filter(Boolean)
            .join(' '),
        },
        {
          title: getStepTitle(1, <Space>
            <Progress type="circle" percent={percentage} size={50}/>
            <ZipIcon size={32}/>
            {file?.name}
          </Space>),
          description: translate('uploading'),
        },
        {
          title: getStepTitle(2, extractOnError
            ? <Button
              type='text'
              shape='circle'
              icon={<RedoOutlined/>}
              onClick={extract}
            />
            : <LoadingOutlined/>),
          description: <span className={extractOnError ? 'text-danger' : ''}>Extracting</span>,
        },
        {
          title: getStepTitle(3, loading ? <LoadingOutlined color='blue'/> : translate('finished')),
          description: translate('comparing'),
        },
      ]}
    />

    <ShowIf
      condition={!!data && currentStep === 3}
      replacement={<></>}
    >
      <Row
        className='mt-3'
      >
        <Col span={12} className='bg-white'>
          <Container
            loading={loading}
          >
            <table className={classNames('table mb-0', style.massUploadSummary)}>
              <tbody className='text-muted'>
              <tr>
                <td className='w-50'>
                  {translate('total_files')}
                </td>

                <td>
                  {data?.massUploadSummary.total}
                </td>
                <td></td>
              </tr>

              <ShowIf
                condition={!!data?.massUploadSummary.skusDoNotHaveAnyImages.length}
                replacement={<></>}
              >
                <tr>
                  <td>
                    {translate('no_images')}
                  </td>
                  <td>
                    {data?.massUploadSummary.skusDoNotHaveAnyImages.length}
                  </td>
                  <td>
                    <Space>
                      <Button
                        disabled={completedActions?.noImages}
                        type='primary'
                        loading={inProgressAddSkuDoNotHaveAnyImages}
                        onClick={() => addSkuDoNotHaveAnyImages()}
                      >
                        {translate('add_to_image_list')}
                      </Button>

                      <CompletedActionIcon completed={!!completedActions?.noImages}/>
                    </Space>
                  </td>
                </tr>
              </ShowIf>

              <ShowIf
                condition={!!data?.massUploadSummary.skusHaveDifferentFileNames.length}
                replacement={<></>}
              >
                <tr>
                  <td
                    onClick={() => setExpand(pre => ({...pre as any, different: !pre?.different}))}
                    style={{cursor: 'pointer'}}
                  >
                    <Space>
                      {expand?.different ? <MinusSquareOutlined/> : <PlusSquareOutlined/>}
                      {translate('different_file_names')}
                    </Space>
                  </td>
                  <td>
                    {data?.massUploadSummary.skusHaveDifferentFileNames.length}
                  </td>
                  <td>
                    <Space>
                      <Button
                        disabled={completedActions?.different}
                        loading={inProgressAddDifferentImagesToItems}
                        onClick={() => addDifferentImagesToItems()}
                      >
                        {translate('add_to_image_list')}
                      </Button>

                      <CompletedActionIcon completed={!!completedActions?.different}/>
                    </Space>
                  </td>
                </tr>
              </ShowIf>

              <ShowIf
                condition={!!expand?.different}
                replacement={<></>}
              >
                <tr className={style.massUploadSummaryDetails}>
                  <td colSpan={3} className='p-3'>
                    <table className='table'>
                      <thead>
                      <tr>
                        <th className='w-25'>{translate('sku')}</th>
                        <th className='w-25'>{translate('persisted')}</th>
                        <th className='w-25'>{translate('uploaded')}</th>
                      </tr>
                      </thead>
                      <tbody>
                      {
                        data?.massUploadSummary.skusHaveDifferentFileNames.map((it, index) =>
                          <tr key={index}>
                            <td>{it.sku}</td>
                            <td>
                              <Space>
                                {
                                  it.persistedImages.map((img, index) =>
                                    <Image
                                      preview={{}}
                                      width={50}
                                      height={50}
                                      src={resolvePathFile(img.fileName)}
                                    />
                                  )
                                }
                              </Space>
                            </td>
                            <td>
                              <Space>
                                {
                                  it.uploadedImages.map((img, index) =>
                                    <Image
                                      width={50}
                                      height={50}
                                      src={resolvePathFile(img.fullPath)}
                                    />
                                  )
                                }
                              </Space>
                            </td>
                          </tr>
                        )
                      }
                      </tbody>
                    </table>
                  </td>
                </tr>
              </ShowIf>

              <ShowIf
                condition={!!data?.massUploadSummary.skusHaveDuplicatedFileNames.length}
                replacement={<></>}
              >
                <tr>
                  <td
                    onClick={() => setExpand(pre => ({...pre as any, duplicated: !pre?.duplicated}))}
                    style={{cursor: 'pointer'}}
                  >
                    <Space>
                      {expand?.duplicated ? <MinusSquareOutlined/> : <PlusSquareOutlined/>}
                      {translate('duplicated_file_names')}
                    </Space>
                  </td>
                  <td>
                    {data?.massUploadSummary.skusHaveDuplicatedFileNames.length}
                  </td>
                  <td>
                    <Space>
                      <Button
                        disabled={completedActions?.duplicated}
                        loading={inProgressReplaceDuplicated}
                        onClick={() => replaceDuplicated()}
                      >
                        {translate('replace_new_images')}
                      </Button>

                      <CompletedActionIcon completed={!!completedActions?.duplicated}/>
                    </Space>
                  </td>
                </tr>
              </ShowIf>

              <ShowIf
                condition={!!expand?.duplicated}
                replacement={<></>}
              >
                <tr className={style.massUploadSummaryDetails}>
                  <td colSpan={3} className='p-3'>
                    <table className='table'>
                      <thead>
                      <tr>
                        <th className='w-25'>{translate('sku')}</th>
                        <th className='w-25'>{translate('file_name')}</th>
                        <th className='w-25'>{translate('persisted')}</th>
                        <th className='w-25'>{translate('uploaded')}</th>
                      </tr>
                      </thead>
                      <tbody>
                      {
                        data?.massUploadSummary.skusHaveDuplicatedFileNames.map((it, index) =>
                          <tr key={index}>
                            <td>{it.sku}</td>
                            <td>{it.uploadedImage.fileName}</td>
                            <td>
                              <Image
                                width={50}
                                height={50}
                                src={resolvePathFile(it.persistedImage.fileName)}
                              />
                            </td>
                            <td>
                              <Image
                                width={50}
                                height={50}
                                src={resolvePathFile(it.uploadedImage.fullPath)}
                              />
                            </td>
                          </tr>
                        )
                      }
                      </tbody>
                    </table>
                  </td>
                </tr>
              </ShowIf>
              <tr>
                <td>
                  {translate('invalid_skus')}
                </td>
                <td>
                  {data?.massUploadSummary.invalidSkus.length}
                </td>
                <td></td>
              </tr>
              </tbody>
            </table>
          </Container>
        </Col>
      </Row>
    </ShowIf>
    {contextHolder}
  </>
}
